// Server functions for template operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { TemplateSchema, TemplateVisibilityEnum, TemplateUsageEnum } from '@/src/types/template.zod'
import { generateShareId } from '@/src/server/lib/share'
import { PERMISSIONS } from '@/permission.config'
import { hasPermission } from '@/src/server/lib/permissions'
import { logResourceAccess, logResourceCreate, logResourceUpdate, logResourceDelete, logUnauthorizedAccess, logAdminAction } from '@/src/server/lib/securityMonitoring'
import { requireUserId, isAdmin } from '@/src/server/lib/accessControl'
import { z } from 'zod'

// Zod schemas for input validation
const GetTemplateByIdInput = z.object({
  id: z.string(),
})

const GetTemplateByShareIdInput = z.object({
  shareId: z.string(),
})

const UpsertTemplateInput = z.object({
  id: z.string().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  content: z.any(),
  undefinedValue: z.string().nullable().optional(),
  type: z.enum(['text/plain', 'application/json', 'text/csv', 'text/markdown', 'application/xml', 'text/html', 'image/svg+xml']).optional(),
  visibility: z.enum(['PRIVATE', 'PUBLIC', 'ORGANIZATION']).optional(),
  usage: z.enum(['RESULTS', 'WORKFLOW', 'EMAIL', 'OTHER']).optional(),
  enabled: z.boolean().optional(),
})

const DeleteTemplateInput = z.object({
  id: z.string(),
})

const RollTemplateShareIdInput = z.object({
  templateId: z.string(),
})

// List templates - filters by permission level
// - Global admins see all templates
// - Org owners see templates from their organization + PUBLIC templates
// - Regular users see enabled PUBLIC templates
export const listTemplates = createServerFn({ method: 'GET' })
  .inputValidator((data?: { all?: boolean }) => data || {})
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    if (data?.all) {
      // Check permission to list all templates
      const isGlobalAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
      const canManageTemplates = await hasPermission(session, [PERMISSIONS.TEMPLATE_MANAGE, PERMISSIONS.ADMIN])
      
      if (!canManageTemplates) {
        await logUnauthorizedAccess(userId, 'template', undefined, { action: 'list all' })
        throw new Error('Unauthorized')
      }
      
      await logAdminAction(userId!, 'List all templates', { includeDisabled: true })
      
      // Build where clause based on permission level
      let whereClause: any = {}
      
      // If not global admin, filter by organization
      if (!isGlobalAdmin && userId) {
        // Org owner: see templates from org members + PUBLIC templates
        const user = await db.user.findUnique({
          where: { id: userId },
          select: { organizationId: true },
        })
        
        if (user?.organizationId) {
          // Get all users in the organization
          const orgUsers = await db.user.findMany({
            where: { organizationId: user.organizationId },
            select: { id: true },
          })
          const orgUserIds = orgUsers.map(u => u.id)
          
          // Filter to templates created by org members or PUBLIC
          whereClause = {
            OR: [
              { createdById: { in: orgUserIds } },
              { visibility: 'PUBLIC' },
            ],
          }
        } else {
          // No organization - see only their own templates + PUBLIC
          whereClause = {
            OR: [
              { createdById: userId },
              { visibility: 'PUBLIC' },
            ],
          }
        }
      }
      
      const templates = await db.template.findMany({
        where: whereClause,
        orderBy: { name: 'asc' },
        include: { createdBy: true },
      })
      
      return templates.map(t => ({
        ...t,
        createdAt: t.createdAt.toISOString(),
      }))
    }
    
    // Regular mode - user's own templates + PUBLIC + ORGANIZATION (if in org)
    const whereClause = userId
      ? await (async () => {
      const user = await db.user.findUnique({
        where: { id: userId },
        select: { organizationId: true },
      })
      
      const orConditions: any[] = [
        { createdById: userId },
        { visibility: 'PUBLIC', enabled: true },
      ]
      
      // Include organization templates if user is in an organization
      if (user?.organizationId) {
        orConditions.push({
          visibility: 'ORGANIZATION',
          enabled: true,
          createdBy: {
            organizationId: user.organizationId,
          },
        })
      }
      
      return { OR: orConditions }
    })()
      : { visibility: 'PUBLIC', enabled: true }
    
    const templates = await db.template.findMany({
      where: whereClause,
      orderBy: { name: 'asc' },
      include: { createdBy: true },
    })
    
    return templates.map(t => ({
      ...t,
      createdAt: t.createdAt.toISOString(),
    }))
  })

export const getTemplateById = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetTemplateByIdInput.parse(data))
  .handler(async ({ data }) => {
    const template = await db.template.findUnique({
      where: { id: data.id },
    })
    if (!template) return null
    return {
      ...template,
      createdAt: template.createdAt.toISOString(),
    }
  })

export const getTemplateByShareId = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetTemplateByShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const template = await db.template.findFirst({
      where: { shareId: data.shareId },
    })
    if (!template) return null
    return {
      ...template,
      createdAt: template.createdAt.toISOString(),
    }
  })

export const upsertTemplate = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpsertTemplateInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = requireUserId(session)
    
    const isGlobalAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    const hasManagePermission = await hasPermission(session, [PERMISSIONS.TEMPLATE_MANAGE, PERMISSIONS.ADMIN])
    
    if (!hasManagePermission) {
      await logUnauthorizedAccess(userId, 'template', data.id, { action: 'upsert' })
      throw new Error('Unauthorized')
    }
    
    if (data.id) {
      // Update existing template - verify ownership or organization access
      const existing = await db.template.findUnique({
        where: { id: data.id },
        include: { createdBy: { select: { organizationId: true } } },
      })
      
      if (!existing) {
        throw new Error('Template not found')
      }
      
      // Check access: owner OR (same organization AND has TEMPLATE_MANAGE)
      const isOwner = existing.createdById === userId
      const user = await db.user.findUnique({ where: { id: userId }, select: { organizationId: true } })
      const sameOrganization = user?.organizationId && existing.createdBy.organizationId === user.organizationId
      
      if (!isGlobalAdmin && !isOwner && !sameOrganization) {
        await logUnauthorizedAccess(userId, 'template', data.id, { 
          action: 'update',
          reason: 'not owner and not in same organization'
        })
        throw new Error('Unauthorized: You can only edit your own templates or templates from your organization')
      }
      
      const t = await db.template.update({
        where: { id: data.id },
        data: {
          name: data.name,
          description: data.description,
          content: data.content,
          undefinedValue: data.undefinedValue ?? null,
          type: data.type ?? undefined,
          visibility: data.visibility ?? TemplateVisibilityEnum.enum.PRIVATE,
          usage: data.usage ?? undefined,
          enabled: data.enabled !== undefined ? data.enabled : existing.enabled,
        },
      })
      
      await logResourceUpdate(userId, 'template', data.id, {
        name: data.name,
        visibility: data.visibility,
      })
      
      return {
        ...t,
        createdAt: t.createdAt.toISOString(),
      }
    } else {
      // Create new template
      const user = await db.user.findUnique({ where: { id: userId } })
      if (!user) {
        throw new Error('User does not exist. Please log in with a valid account.')
      }
      const shareId = await generateShareId({
        prefix: 'T',
        length: 6,
        isIdAvailable: async (id) => !(await db.template.findUnique({ where: { shareId: id } })),
      })
      const t = await db.template.create({
        data: {
          name: data.name,
          description: data.description,
          content: data.content,
          undefinedValue: data.undefinedValue ?? null,
          type: data.type ?? 'text/plain',
          visibility: data.visibility ?? TemplateVisibilityEnum.enum.PRIVATE,
          usage: data.usage ?? TemplateUsageEnum.enum.RESULTS,
          createdById: userId,
          enabled: data.enabled ?? true,
          shareId,
        },
      })
      
      await logResourceCreate(userId, 'template', t.id, {
        name: data.name,
        visibility: data.visibility,
      })
      
      return {
        ...t,
        createdAt: t.createdAt.toISOString(),
      }
    }
  })

export const deleteTemplate = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => DeleteTemplateInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = requireUserId(session)
    
    const isGlobalAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    const hasManagePermission = await hasPermission(session, [PERMISSIONS.TEMPLATE_MANAGE, PERMISSIONS.ADMIN])
    
    if (!hasManagePermission) {
      await logUnauthorizedAccess(userId, 'template', data.id, { action: 'delete' })
      throw new Error('Unauthorized')
    }
    
    // Verify ownership or organization access
    const template = await db.template.findUnique({
      where: { id: data.id },
      include: { createdBy: { select: { organizationId: true } } },
    })
    
    if (!template) {
      throw new Error('Template not found')
    }
    
    const isOwner = template.createdById === userId
    const user = await db.user.findUnique({ where: { id: userId }, select: { organizationId: true } })
    const sameOrganization = user?.organizationId && template.createdBy.organizationId === user.organizationId
    
    if (!isGlobalAdmin && !isOwner && !sameOrganization) {
      await logUnauthorizedAccess(userId, 'template', data.id, { 
        action: 'delete',
        reason: 'not owner and not in same organization'
      })
      throw new Error('Unauthorized: You can only delete your own templates or templates from your organization')
    }
    
    await db.template.delete({ where: { id: data.id } })
    await logResourceDelete(userId, 'template', data.id, {})
    
    return { success: true }
  })

export const rollTemplateShareId = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => RollTemplateShareIdInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    if (!session?.user?.id) throw new Error('Unauthorized')
    const userId = session.user.id

    const template = await db.template.findUnique({ 
      where: { id: data.templateId },
      include: { createdBy: { select: { organizationId: true } } },
    })
    if (!template) throw new Error('Template not found')

    const isOwner = template.createdById === userId
    const isAdmin = await hasPermission(session, [PERMISSIONS.ADMIN])
    const user = await db.user.findUnique({ where: { id: userId }, select: { organizationId: true } })
    const sameOrganization = user?.organizationId && template.createdBy.organizationId === user.organizationId
    const hasManagePermission = await hasPermission(session, [PERMISSIONS.TEMPLATE_MANAGE])
    
    if (!isOwner && !isAdmin && !(sameOrganization && hasManagePermission)) {
      throw new Error('Unauthorized: You can only rotate shareId for your own templates or templates from your organization')
    }

    const newShareId = await generateShareId({
      prefix: 'T',
      length: 6,
      isIdAvailable: async (id) => !(await db.template.findUnique({ where: { shareId: id } })),
    })

    await db.template.update({ where: { id: data.templateId }, data: { shareId: newShareId } })
    return { shareId: newShareId }
  })
